import './RTUFilter.css'; // Make sure the CSS file is correctly referenced

import React, { useRef, useState } from 'react';

import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { defaultThemeColor } from '../../../config/themeConfig';
import { buildAssetStatusFilterOptions } from '../../../utils/statusConfig';
import { useTranslation } from 'react-i18next';

interface RTUFilterProps {
  setFilters: (newFilters: any) => void;
  clearFilters: () => void;
  // Distinct Location / Producer / Field values from the loaded fleet, for suggestions.
  locationOptions: string[];
  producerOptions: string[];
  fieldOptions: string[];
}

const COMPARISON_OPTIONS = [
  { label: 'Greater than (>)', value: '>' },
  { label: 'Less than (<)', value: '<' },
];

// Shared class so the portal-rendered overlay panels can be sized down to match
// the rest of the compact filter panel (they render outside .filter-container).
const PANEL_CLASS = 'map-filter-panel';

const RTUFilter: React.FC<RTUFilterProps> = ({ setFilters, clearFilters, locationOptions, producerOptions, fieldOptions }) => {
  const { t } = useTranslation();
  const statusOptions = [
    ...buildAssetStatusFilterOptions(t),
    { label: 'All', value: 'all' },
  ];
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [locationFilter, setLocationFilter] = useState('');
  const [producerFilter, setProducerFilter] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);
  const [producerSuggestions, setProducerSuggestions] = useState<string[]>([]);
  const [fieldSuggestions, setFieldSuggestions] = useState<string[]>([]);
  // Refs used to open the suggestion overlay when the input is focused/clicked
  // (PrimeReact's AutoComplete only opens the panel from its own search()).
  const locationRef = useRef<any>(null);
  const producerRef = useRef<any>(null);
  const fieldRef = useRef<any>(null);

  const [cpuUsageFilter, setCpuUsageFilter] = useState({ comparison: '>', value: 0 });
  const [diskUsageFilter, setDiskUsageFilter] = useState({ comparison: '>', value: 0 });

  const onStatusChange = (e: any) => {
    setSelectedStatus(e.value);
  };

  const completeFrom = (options: string[], query: string): string[] => {
    const q = (query || '').toLowerCase();
    if (!q) return options;
    return options.filter((option) => option.toLowerCase().includes(q));
  };

  const onComparisonChange = (type: 'cpu' | 'disk', comparison: '>' | '<') => {
    if (type === 'cpu') {
      setCpuUsageFilter(prev => ({ ...prev, comparison }));
    } else {
      setDiskUsageFilter(prev => ({ ...prev, comparison }));
    }
  };

  const onValueChange = (type: 'cpu' | 'disk', value: string) => {
    const numValue = parseInt(value) || 0;
    if (type === 'cpu') {
      setCpuUsageFilter(prev => ({ ...prev, value: numValue }));
    } else {
      setDiskUsageFilter(prev => ({ ...prev, value: numValue }));
    }
  };

  const applyFilters = () => {
    setFilters({
      status: selectedStatus,
      cpuUsage: cpuUsageFilter,
      diskUsage: diskUsageFilter,
      location: locationFilter,
      producer: producerFilter,
      field: fieldFilter,
    });
  };

  // Resets every local filter field back to its neutral value and clears
  // the applied filter on the parent (full fleet + hasAppliedFilter=false).
  const handleClearAll = () => {
    setSelectedStatus(null);
    setLocationFilter('');
    setProducerFilter('');
    setFieldFilter('');
    setCpuUsageFilter({ comparison: '>', value: 0 });
    setDiskUsageFilter({ comparison: '>', value: 0 });
    clearFilters();
  };

  return (
    <div className="filter-container">
      <h5>Filter RTUs</h5>

      {/* Status Dropdown */}
      <div className="p-field">
        <label htmlFor="status">Status</label>
        <Dropdown
          value={selectedStatus}
          options={statusOptions}
          onChange={onStatusChange}
          placeholder="Select a Status"
          panelClassName={PANEL_CLASS}
        />
      </div>

      {/* Location (with suggestions) */}
      <div className="p-field">
        <label htmlFor="location">Location</label>
        <AutoComplete
          ref={locationRef}
          value={locationFilter}
          suggestions={locationSuggestions}
          completeMethod={(e) => setLocationSuggestions(completeFrom(locationOptions, e.query))}
          onChange={(e) => setLocationFilter(e.value)}
          onFocus={(e) => locationRef.current?.search(e, locationFilter || '', 'dropdown')}
          placeholder="Search Location"
          delay={0}
          panelClassName={PANEL_CLASS}
        />
      </div>

      {/* Producer (with suggestions) */}
      <div className="p-field">
        <label htmlFor="producer">Producer</label>
        <AutoComplete
          ref={producerRef}
          value={producerFilter}
          suggestions={producerSuggestions}
          completeMethod={(e) => setProducerSuggestions(completeFrom(producerOptions, e.query))}
          onChange={(e) => setProducerFilter(e.value)}
          onFocus={(e) => producerRef.current?.search(e, producerFilter || '', 'dropdown')}
          placeholder="Search Producer"
          delay={0}
          panelClassName={PANEL_CLASS}
        />
      </div>

      {/* Field (with suggestions) */}
      <div className="p-field">
        <label htmlFor="field">Field</label>
        <AutoComplete
          ref={fieldRef}
          value={fieldFilter}
          suggestions={fieldSuggestions}
          completeMethod={(e) => setFieldSuggestions(completeFrom(fieldOptions, e.query))}
          onChange={(e) => setFieldFilter(e.value)}
          onFocus={(e) => fieldRef.current?.search(e, fieldFilter || '', 'dropdown')}
          placeholder="Search Field"
          delay={0}
          panelClassName={PANEL_CLASS}
        />
      </div>

  {/* CPU Usage */}
  <div className="p-field-range filter-dropdown-input-group">
    <label htmlFor="cpuUsageFilter">CPU Usage (<b>X</b>%)</label>
    <div className="p-inputgroup">
      <Dropdown
        className="dropdown"
        value={cpuUsageFilter.comparison}
        options={COMPARISON_OPTIONS}
        onChange={(e) => onComparisonChange('cpu', e.value)}
        panelClassName={PANEL_CLASS}
      />
      <InputText
        value={cpuUsageFilter.value.toString()}
        onChange={(e) => onValueChange('cpu', e.target.value)}
        type="number"
        min="0"
        max="100"
      />
    </div>
  </div>

  {/* Disk Usage */}
  <div className="p-field-range filter-dropdown-input-group">
    <label htmlFor="diskUsageFilter">Disk Usage (<b>X</b>%)</label>
    <div className="p-inputgroup">
      <Dropdown
        value={diskUsageFilter.comparison}
        options={COMPARISON_OPTIONS}
        onChange={(e) => onComparisonChange('disk', e.value)}
        panelClassName={PANEL_CLASS}
      />
      <InputText
        value={diskUsageFilter.value.toString()}
        onChange={(e) => onValueChange('disk', e.target.value)}
        type="number"
        min="0"
        max="100"
      />
    </div>
  </div>
    <div className="filter-actions">
      <Button label="Apply" icon="pi pi-check" onClick={applyFilters} style={{ backgroundColor: defaultThemeColor, borderColor: defaultThemeColor }} />
      <Button label="Clear" icon="pi pi-times" className="p-button-outlined" onClick={handleClearAll} />
    </div>
    </div>
  );
};

export default RTUFilter;
